import { auth, db } from '../firebase/config';
import { createUserProfile, getUserProfile, COLLECTIONS } from '../firebase/collections';
import { collection, query, where, getDocs, limit, updateDoc, doc } from 'firebase/firestore';

/**
 * Creates a user profile if it doesn't exist
 * This is useful for users who signed up before profile creation was implemented
 */
export const createMissingUserProfile = async (user) => {
  try {
    if (!user || !user.uid) {
      return { success: false, error: 'No user provided' };
    }

    // Check if profile already exists
    const existingProfile = await getUserProfile(user.uid);
    
    if (existingProfile.success) {
      const profile = existingProfile.data;
      
      // If coachingId is missing in profile, check students and enrollments collections
      if (!profile.coachingId && (profile.role === 'student' || !profile.role)) {
        try {
          let foundCoachingId = null;
          const userEmail = user.email.toLowerCase().trim();

          // Step 1: Check students collection
          const studentsRef = collection(db, 'students');
          const studentQuery = query(studentsRef, where('email', '==', userEmail), limit(1));
          const studentSnapshot = await getDocs(studentQuery);
          
          if (!studentSnapshot.empty) {
            foundCoachingId = studentSnapshot.docs[0].data().coachingId;
          }

          // Step 2: If not found, check batch enrollments (since student might already see batches)
          if (!foundCoachingId) {
            const enrollmentsRef = collection(db, 'batchEnrollments');
            const enrollmentQuery = query(enrollmentsRef, where('email', '==', userEmail), limit(1));
            const enrollmentSnapshot = await getDocs(enrollmentQuery);
            
            if (!enrollmentSnapshot.empty) {
              foundCoachingId = enrollmentSnapshot.docs[0].data().coachingId;
            }
          }
          
          if (foundCoachingId) {
            // Update existing profile with coachingId
            await updateDoc(doc(db, COLLECTIONS.USERS || 'users', user.uid), {
              coachingId: foundCoachingId,
              updatedAt: new Date()
            });
            profile.coachingId = foundCoachingId;
            console.log(`Updated existing profile for ${user.email} with coachingId: ${foundCoachingId}`);
          }
        } catch (error) {
          console.error('Error checking for coachingId for existing profile:', error);
        }
      }
      
      return { success: true, data: profile, alreadyExists: true };
    }

    // CRITICAL: Only proceed to create a new profile if it's explicitly not found
    // If there was a network error or other issue, we should NOT create a new profile
    // as it might overwrite an existing one with default values.
    if (existingProfile.error !== 'User not found' && existingProfile.error?.message !== 'User not found') {
      console.warn('⚠️ Skipping profile creation due to fetch error:', existingProfile.error);
      return { success: false, error: existingProfile.error };
    }

    // NEW PROFILE CREATION LOGIC
    // Check if student exists in either collection to link coachingId immediately
    let coachingId = null;
    let studentName = null;
    let studentPhone = null;
    const userEmail = user.email.toLowerCase().trim();

    try {
      // Check students collection first
      const studentsRef = collection(db, 'students');
      const studentQuery = query(studentsRef, where('email', '==', userEmail), limit(1));
      const studentSnapshot = await getDocs(studentQuery);
      
      if (!studentSnapshot.empty) {
        const studentData = studentSnapshot.docs[0].data();
        coachingId = studentData.coachingId;
        studentName = studentData.name;
        studentPhone = studentData.phone;
      }

      // Check batch enrollments if not found in students
      if (!coachingId) {
        const enrollmentsRef = collection(db, 'batchEnrollments');
        const enrollmentQuery = query(enrollmentsRef, where('email', '==', userEmail), limit(1));
        const enrollmentSnapshot = await getDocs(enrollmentQuery);
        
        if (!enrollmentSnapshot.empty) {
          coachingId = enrollmentSnapshot.docs[0].data().coachingId;
        }
      }
      
      if (coachingId) {
        console.log(`Linking new profile to coachingId: ${coachingId}`);
      }
    } catch (error) {
      console.error('Error checking collections during profile creation:', error);
    }

    // Create new profile
    // Determine role based on email patterns
    let role = 'student'; // Default to student
    let userType = 'student';
    
    if (user.email.includes('admin') || user.email.includes('admin@')) {
      role = 'admin';
      userType = 'admin';
    } else if (user.email.includes('teacher') || user.email.includes('teach@')) {
      role = 'teacher';
      userType = 'teacher';
    }
    
    const userProfileData = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || studentName || user.email.split('@')[0],
      name: user.displayName || studentName || user.email.split('@')[0],
      phone: studentPhone || '',
      role: role,
      userType: userType, 
      coachingId: coachingId, // Link to coaching if found
      isActive: true,
      profileCompleted: false,
      performance: {
        totalTestsTaken: 0,
        averageScore: 0,
        totalScore: 0,
        totalCorrectAnswers: 0,
        totalQuestions: 0,
        lastTestDate: null
      },
      preferences: {
        theme: 'light',
        notifications: true,
        emailNotifications: true,
        selectedExams: [],
        activeExam: null
      },
      // Add teacher-specific data if role is teacher
      ...(role === 'teacher' && {
        teacherProfile: {
          subjects: [],
          experience: 0,
          qualifications: [],
          batches: [],
          salary: {
            amount: 0,
            currency: 'INR',
            paymentSchedule: 'monthly'
          },
          attendance: {
            totalDays: 0,
            presentDays: 0,
            leaves: []
          }
        }
      }),
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const profileResult = await createUserProfile(userProfileData);
    
    if (profileResult.success) {
      return { success: true, data: userProfileData, alreadyExists: false };
    } else {
      return { success: false, error: profileResult.error };
    }
  } catch (error) {
    return { success: false, error };
  }
};

/**
 * Ensures user profile exists, creates if missing
 * This should be called after successful authentication
 */
export const ensureUserProfile = async () => {
  try {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return { success: false, error: 'No authenticated user' };
    }

    return await createMissingUserProfile(currentUser);
  } catch (error) {
    return { success: false, error };
  }
};
